import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../api';
import {
  ResponseArtifactDTO,
  UpdateResponseArtifactRequest,
  UpdateResponseArtifactResponse,
  GetUserArtifactsResponse,
  ResponseArtifactCreatedEvent,
  DesiredAction,
} from '@dreamai/types';
import { useResponseArtifactEvents } from '../../../providers';
import ResearchBlockModal from './ResearchBlockModal';
import { ReactComponent as DocumentTextIcon } from '../../../icons/document-text.svg';
import { ReactComponent as LoadingRingsIcon } from '../../../icons/loading-rings.svg';

interface ProjectAppResearchSectionProps {
  threadId: string | null;
  threadName?: string;
  headingLabel?: string;
  showEmptyState?: boolean;
}

const ProjectAppResearchSection: React.FC<ProjectAppResearchSectionProps> = ({
  threadId,
  threadName,
  headingLabel = 'Project research',
  showEmptyState = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();
  const [artifacts, setArtifacts] = useState<ResponseArtifactDTO[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [blockModalAppId, setBlockModalAppId] = useState<string | null>(null);
  const [blockModalTitle, setBlockModalTitle] = useState<string | undefined>(
    undefined,
  );
  const [blockModalArtifactId, setBlockModalArtifactId] = useState<
    string | null
  >(null);
  const [blockModalDesiredActions, setBlockModalDesiredActions] = useState<
    DesiredAction[] | undefined
  >(undefined);
  const [completingItems, setCompletingItems] = useState<Set<string>>(
    new Set(),
  );
  const [enteringItems, setEnteringItems] = useState<Set<string>>(new Set());

  const fetchArtifacts = useCallback(async () => {
    if (!threadId) return;
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetUserArtifactsResponse>(
        `/response-artifacts/user/artifacts?status=pending&status=processing&artifactType=information_dump&parentThreadId=${threadId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setArtifacts(response.data.artifacts || []);
    } catch (err) {
      console.error('Error fetching research artifacts:', err);
    }
  }, [threadId, getAccessTokenSilently]);

  useEffect(() => {
    fetchArtifacts();
  }, [fetchArtifacts]);

  // Auto-open research document from URL param (e.g. navigated from dashboard)
  useEffect(() => {
    const researchAppId = searchParams.get('research_app_id');
    if (researchAppId && artifacts.length > 0 && !blockModalAppId) {
      const match = artifacts.find((a) => a.projectAppId === researchAppId);
      if (match) {
        setBlockModalAppId(researchAppId);
        setBlockModalTitle(match.title);
        setBlockModalDesiredActions(match.desiredActions);
      } else {
        setBlockModalAppId(researchAppId);
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('research_app_id');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, artifacts, blockModalAppId, setSearchParams]);

  // Listen for new research artifacts via provider
  useEffect(() => {
    if (!threadId) return;
    const callbackId = `research-section-${threadId}`;

    registerCreatedCallback(
      callbackId,
      (data: ResponseArtifactCreatedEvent) => {
        if (data.parentThreadId !== threadId) return;
        const artifact = data.artifact as ResponseArtifactDTO;
        if (artifact?.artifactType !== 'information_dump') return;
        setArtifacts((prev) => {
          if (prev.some((a) => a.id === artifact.id)) return prev;
          return [artifact, ...prev];
        });
        setEnteringItems((prev) => new Set(prev).add(artifact.id));
      },
    );

    registerUpdatedCallback(callbackId, (data: any) => {
      if (data.parentThreadId !== threadId) return;
      if (data.artifactType !== 'information_dump') return;

      if (data.status === 'pending') {
        setArtifacts((prev) =>
          prev.map((a) =>
            a.id === data.artifactId ? { ...a, status: 'pending' } : a,
          ),
        );
      } else if (data.status === 'actioned') {
        setArtifacts((prev) => prev.filter((a) => a.id !== data.artifactId));
      }
    });

    return () => {
      unregisterCreatedCallback(callbackId);
      unregisterUpdatedCallback(callbackId);
    };
  }, [
    threadId,
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  ]);

  const handleOpen = useCallback(
    async (artifact: ResponseArtifactDTO) => {
      if (artifact.projectAppId) {
        setBlockModalAppId(artifact.projectAppId);
        setBlockModalTitle(artifact.title);
        setBlockModalDesiredActions(artifact.desiredActions);
      }

      // Optimistically mark as read
      if (artifact.unread) {
        setArtifacts((prev) =>
          prev.map((a) => (a.id === artifact.id ? { ...a, unread: false } : a)),
        );

        try {
          const token = await getAccessTokenSilently();
          await api.patch(
            `/response-artifacts/${artifact.id}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch (error) {
          console.error('Error marking artifact as read:', error);
        }
      }
    },
    [getAccessTokenSilently],
  );

  const handleDismiss = useCallback(
    async (e: React.MouseEvent, artifact: ResponseArtifactDTO) => {
      e.stopPropagation();
      setCompletingItems((prev) => new Set(prev).add(artifact.id));
      setTimeout(() => {
        setCompletingItems((prev) => {
          const next = new Set(prev);
          next.delete(artifact.id);
          return next;
        });
        setArtifacts((prev) => prev.filter((a) => a.id !== artifact.id));
      }, 400);

      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: { dismissed: true },
          source: 'todo_list',
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (error) {
        console.error('Error dismissing research artifact:', error);
      }
    },
    [getAccessTokenSilently],
  );

  if (artifacts.length === 0) {
    if (!showEmptyState) return null;
    return (
      <div className='project-app-empty-state'>
        <div className='project-app-empty-state__card'>
          <div className='project-app-empty-state__icon' aria-hidden='true'>
            <DocumentTextIcon />
          </div>
          <h2 className='project-app-empty-state__title'>No documents yet</h2>
          <p className='project-app-empty-state__description'>
            Research and documents created during this project will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='project-app-research-section'>
      <h2 className='project-app-block-heading project-app-block-heading--h2'>
        {headingLabel}
      </h2>
      <div className='project-app-research-cards'>
        {artifacts.map((artifact, index) => {
          const isCompleting = completingItems.has(artifact.id);
          return (
            <div
              key={artifact.id}
              className={`project-app-research-card${isCompleting ? ' completing' : ''}${enteringItems.has(artifact.id) ? ' entering' : ''}`}
              onClick={() => handleOpen(artifact)}
              role='button'
              tabIndex={0}
              onAnimationEnd={() => {
                setEnteringItems((prev) => {
                  const next = new Set(prev);
                  next.delete(artifact.id);
                  return next;
                });
              }}
            >
              <div className='research-card-top'>
                {artifact.status === 'processing' ? (
                  <LoadingRingsIcon />
                ) : (
                  <DocumentTextIcon />
                )}
                {artifact.unread && (
                  <span className='research-card-unread-badge'>Unread</span>
                )}
              </div>
              <div className='research-card-bottom'>
                {artifact.title || 'Untitled'}
              </div>
            </div>
          );
        })}
      </div>
      {blockModalAppId && (
        <ResearchBlockModal
          show={!!blockModalAppId}
          onClose={() => {
            setBlockModalAppId(null);
            setBlockModalDesiredActions(undefined);
          }}
          appId={blockModalAppId}
          title={blockModalTitle}
          desiredActions={blockModalDesiredActions}
          parentThreadId={threadId || undefined}
          backLabel={threadName}
        />
      )}
    </div>
  );
};

export default ProjectAppResearchSection;
